import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import enTranslations from './translations.en';

type Lang = 'en' | 'de';

// English is bundled inline and German is loaded on demand.
const loadedTranslations: Record<string, Record<string, string>> = {
  en: enTranslations,
};

// Lazy loader for German.
const langLoaders: Record<string, () => Promise<{ default: Record<string, string> }>> = {
  de: () => import('./translations.de'),
};

interface LanguageContextType {
  t: (key: string) => string;
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>('en');
  const [, forceUpdate] = useState(0);

  const setLang = async (newLang: Lang) => {
    if (newLang === 'en' || loadedTranslations[newLang]) {
      setLangState(newLang);
      return;
    }
    // Lazy-load the translation file
    const loader = langLoaders[newLang];
    if (loader) {
      const mod = await loader();
      loadedTranslations[newLang] = mod.default;
      setLangState(newLang);
      forceUpdate(n => n + 1);
    }
  };

  const t = (key: string): string => {
    const dict = loadedTranslations[lang] || loadedTranslations['en'];
    return dict[key] || loadedTranslations['en'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ t, lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
